
/**
 * Main entry point for guard services - exports all guard-related functionality
 */

// Re-export all guard services
export { 
  getGuardsInUserTeams, 
  getGuardsByTeamId, 
  getUserTeamIds,
  getArchivedGuards 
} from './guardFetchService';
export { createGuardWithTeam } from './guardCreationService';
export { assignGuardToTeam } from './teamService';
export { archiveGuard } from './guardArchiveService';
export { restoreGuard } from './guardRestoreService';

import { supabase } from "@/integrations/supabase/client";
import { Guard } from "../types";

export const updateGuard = async (guardId: string, updates: Partial<Guard>) => {
  // Validate dates before sending to the server
  const sanitizedUpdates = { ...updates };
  
  // Convert empty date strings to null to avoid invalid date errors
  ['hiring_date', 'date_of_birth', 'license_expiry_date'].forEach(field => {
    if (sanitizedUpdates[field] === '') {
      sanitizedUpdates[field] = null;
    }
    
    // Validate date fields
    if (sanitizedUpdates[field]) {
      const date = new Date(sanitizedUpdates[field]);
      if (isNaN(date.getTime()) || date.getFullYear() < 1900) {
        sanitizedUpdates[field] = null;
      }
    }
  });

  // First, if email is being updated, we need to get the current guard's email
  if (sanitizedUpdates.email !== undefined) {
    // Get current guard data
    const { data: currentGuardData, error: fetchError } = await supabase
      .from('profiles')
      .select('email')
      .eq('id', guardId)
      .single();

    if (fetchError) {
      console.error('Error fetching current guard data:', fetchError);
      throw fetchError;
    }

    // Check if email is actually changing
    if (currentGuardData && currentGuardData.email !== sanitizedUpdates.email) {
      try {
        // Call the edge function to update the email in auth system
        const { error: emailUpdateError } = await supabase.functions.invoke('update-guard-email', {
          body: { userId: guardId, newEmail: sanitizedUpdates.email }
        });
        
        if (emailUpdateError) {
          console.error('Error updating guard email in auth system:', emailUpdateError);
          throw emailUpdateError;
        }
        
        console.log('Successfully updated guard email in auth system');
      } catch (emailError) {
        console.error('Failed to update guard email in auth system:', emailError);
        throw emailError;
      }
    }
  }

  // If Role is being updated, we need to update the user_roles table
  if (sanitizedUpdates.Role !== undefined) {
    try {
      // First, check if a role record exists
      const { data: existingRoleData, error: roleCheckError } = await supabase
        .from('user_roles')
        .select('id')
        .eq('user_id', guardId);
      
      if (roleCheckError) {
        console.error('Error checking existing role:', roleCheckError);
        throw roleCheckError;
      }

      if (existingRoleData && existingRoleData.length > 0) {
        // Update existing role
        const { error: roleUpdateError } = await supabase
          .from('user_roles')
          .update({ role: sanitizedUpdates.Role })
          .eq('user_id', guardId);
        
        if (roleUpdateError) {
          console.error('Error updating user role:', roleUpdateError);
          throw roleUpdateError;
        }
      } else {
        // Insert new role
        const { error: roleInsertError } = await supabase
          .from('user_roles')
          .insert({ user_id: guardId, role: sanitizedUpdates.Role });
        
        if (roleInsertError) {
          console.error('Error inserting user role:', roleInsertError);
          throw roleInsertError;
        }
      }

      console.log(`Successfully updated role to ${sanitizedUpdates.Role} for user ${guardId}`);
    } catch (roleError) {
      console.error('Failed to update user role:', roleError);
      throw roleError;
    }
  }

  // Proceed with regular profile update
  const { data, error } = await supabase
    .from('profiles')
    .update(sanitizedUpdates)
    .eq('id', guardId)
    .select();

  if (error) {
    console.error('Error updating guard profile:', error);
    throw error;
  }

  // Dispatch event to refresh scheduling data when guard info changes
  document.dispatchEvent(new CustomEvent('refetch-schedules'));

  return data;
};
